import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import { useAdBaseData } from './useAdBaseData';
import { useAdRelatedData } from './useAdRelatedData';
import { useAdUiFlags } from './useAdUiFlags';
import { getVisibleShop } from './adShop.helpers';
import { Footer } from '@/components/Footer/Footer';
import { PageTopControls } from '@/components/PageTopControls';
import { Breadcrumbs } from '@/components/Breadcrumbs/Breadcrumbs';
import { Button } from '@/components/UI/Button/Button';
import { HeartIcon, ShareIcon } from '@/components/UI/Icons/Icons';
import * as S from '@/views/Ads/Ad.styles';
import { MainInfo } from '@/components/Ad/MainInfo/MainInfo';
import { Status } from '@/components/Ad/Status/Status';
import { Settings } from '@/components/Ad/Settings/Settings';
import { Properties } from '@/components/Ad/Properties/Properties';
import { Description } from '@/components/Ad/Description/Description';
import { AuthorInfo } from '@/components/Ad/AuthorInfo/AuthorInfo';
import { Complaint } from '@/components/Ad/Complaint';
import { Overview } from '@/components/Ad/Overview/Overview';
import { SliderWithGallery } from '@/components/Ad/SliderWithGallery/SliderWithGallery';
import { AdStatus } from '@/types';
import { ErrorView } from '@/views/Error/Error.view';
import { FloatingButtons } from '@/components/Ad/FloatingButtons/FloatingButtons';
import { useShareData } from '@/hooks/useShareData';
import { MAX_RELATED_ADS } from '@/views/Ads/Ad.view.constants';
import { useToastStore } from '@/store/useToastStore';
import { WarningPendingState } from '@/components/Ad/WarningPendingState/WarningPendingState';
import { useIsAuthor } from '@/hooks/useIsAuthor';
import { useIsShop } from '@/hooks/useIsShop';
import { useBanner } from '@/hooks/useBanner';
import { Toast } from '@/components/UI/Toast';
import { WebViewPaymentStatusModal } from '@/components/Payment/WebViewPaymentStatusModal/WebViewPaymentStatusModal';
import { PaymentInTerminalsAfterAuth } from '@/components/Payment/PaymentInTerminalsAfterAuth';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { extractTargetParams } from '@/utils/helpers';
import { useBookmark } from '@/hooks/useBookmark';
import { SuccessPaymentStatusModal } from '@/components/Payment/SuccessPaymentStatusModal';
import { Header } from '@/components/Header';
import { getCurrentCategoryPath } from '@/hooks/useCurrentCategory/useCurrentCategory.helpers';
import { buildBreadcrumbs } from '@/components/Breadcrumbs/Breadcrumbs.helpers';
import {
  CollectionMasterModal,
  CollectionMasterSheet,
} from '@/features/BookmarkCollections/components';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import NoSsr from '@/components/NoSsr/NoSsr';
import { TransitionSourceBlockEnum } from '@/api/types/ads.types';
import { useResponsive } from '@/hooks/useResponsive';
import { Statistics } from '@/components/Ad/Statistics/Statistics';
import { PriceAndContacts } from '@/components/Ad/PriceAndContacts/PriceAndContacts';
import { RelatedAdsSkeletonLoader } from '@/components/Ad/RelatedAds/RelatedAdsSkeletonLoader';
import { MissingAdNotice } from '@/components/Ad/MissingAdNotice';
import { useUserFavorites } from '@/features/BookmarkCollections/hooks';
import { markAdAsViewed } from '@/hooks/useViewedAds/useViewedAds.helpers';
import { useIosNudgeScrollOffTop } from '@/hooks/useIosNudgeScrollOffTop';

const CategoryPathSynchronizer = dynamic(
  () =>
    import('@/components/CategoryPathSynchronizer').then(
      mod => mod.CategoryPathSynchronizer
    ),
  { ssr: false }
);

const RelatedAds = dynamic(
  () => import('@/components/Ad/RelatedAds').then(mod => mod.RelatedAds),
  {
    ssr: false,
    loading: () => <RelatedAdsSkeletonLoader />,
  }
);

export const AdView = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const router = useRouter();
  const { isDesktop } = useResponsive();
  const [isGalleryOpen, toggleGallery] = useToggleOpenState();
  const [isCollectionMasterOpen, setIsCollectionMasterOpen] = useState(false);
  const { openToast, closeToast, onAnimationComplete } = useToastStore();
  const { shareUrl, canShare } = useShareData();
  const adBaseData = useAdBaseData();
  const { adData, loading, status, currentCategory } = adBaseData;
  const visibleShop = getVisibleShop(adData);
  const shopForRelatedAds =
    adData?.features?.adFeatures?.hasSelfOtherAds === false
      ? null
      : visibleShop;
  const isAuthor = useIsAuthor({ cacheOnly: true });
  const isShop = useIsShop(adData, { cacheOnly: true });
  const {
    relatedAdsList,
    relatedAdsLoading,
    isRelatedAdsVisible,
    hasShowAllLink,
    relatedAdsShowAllLink,
    hasVisibleShop,
  } = useAdRelatedData(adBaseData, isAuthor, shopForRelatedAds);
  const {
    canComplain,
    canBookmark,
    shouldShowNotFound,
    shouldShowBanner,
    hasFloatingButtons,
    shouldShowBurgerInHeader,
    isLoadedWithoutAdData,
  } = useAdUiFlags(adBaseData, isAuthor, isShop);
  const { refetch } = useUserFavorites({ skip: true });
  const { isBookmarked: isFavorite, toggleBookmark } = useBookmark({
    ad: adData ?? undefined,
  });
  const iosNudgeScrollOffTop = useIosNudgeScrollOffTop();

  const targetParams = useMemo(
    () => extractTargetParams(adData ?? undefined),
    [adData]
  );

  const { topBanner, backgroundBanner, catfishBanner } = useBanner({
    isDesktop,
    targetParams: targetParams || {},
    canLoadTargetedBanners: !loading && !!targetParams,
  });

  const isShareVisible = canShare && status === AdStatus.Published;

  async function handleBookmarkClick() {
    await toggleBookmark();
    await refetch();

    if (!isFavorite) {
      handleAddToCollection();
    } else {
      closeToast();
      onAnimationComplete();

      setTimeout(() => {
        openToast({
          isLight: isGalleryOpen,
          text: t('bookmark_collections.unbookmarked'),
        });
      }, 50);
    }
  }

  function handleAddToCollection() {
    closeToast();
    onAnimationComplete();

    setTimeout(() => {
      openToast({
        isLight: isGalleryOpen,
        text: t('bookmark_collections.toast_tip'),
        action: t('bookmark_collections.toast_action'),
        onActionClick: handleToastActionClick,
      });
    }, 50);
  }

  function handleToastActionClick() {
    closeToast();
    toggleGallery(false);
    iosNudgeScrollOffTop();
    setIsCollectionMasterOpen(true);
  }

  useEffect(() => {
    closeToast();
  }, [isGalleryOpen]);

  useEffect(() => {
    markAdAsViewed(adData?.id);
  }, [adData?.id]);

  // TODO: set_edit_limit_reached param needed only for rails ad page. Remove this after implementing responsive ad page
  useEffect(() => {
    if (router.query.set_edit_limit_reached) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars,camelcase
      const { set_edit_limit_reached, ...cleanQuery } = router.query;
      const currentHash = window.location.hash;
      router.replace({ query: cleanQuery, hash: currentHash }, undefined, {
        shallow: true,
      });
    }
  }, [router]);

  function renderHeaderActions() {
    if (loading && !adData) {
      return (
        <S.AdHeaderActions>
          <SkeletonLoader width="17px" height="20px" />
          <SkeletonLoader width="20px" height="20px" />
        </S.AdHeaderActions>
      );
    }

    return (
      <S.AdHeaderActions>
        {isShareVisible && (
          <Button
            appearance="transparent"
            hasPadding={false}
            onClick={shareUrl}
            aria-label="Share"
          >
            <ShareIcon
              stroke={theme.palette.primary.labels}
              className="share-btn js-share-btn"
            />
          </Button>
        )}

        {canBookmark && (
          <Button
            appearance="transparent"
            aria-label="Toggle bookmark"
            hasPadding={false}
            onClick={handleBookmarkClick}
            data-stat={
              !isFavorite && isShop ? 'shop-ad-bookmarking' : undefined
            }
            className={!isFavorite ? 'add_bookmark' : undefined}
          >
            <HeartIcon
              data-testid={AdPageTestIds.headerActions.bookmarkIcon}
              fill={isFavorite ? theme.palette.brand.main : 'none'}
              stroke={
                isFavorite
                  ? theme.palette.brand.main
                  : theme.palette.primary.labels
              }
            />
          </Button>
        )}
      </S.AdHeaderActions>
    );
  }

  if (shouldShowNotFound) {
    return (
      <ErrorView
        title={t('pages.ad.nothing_found')}
        subtitle={t('pages.ad.nothing_found_ad_description')}
        hasTopBanner
        hasFooter
      />
    );
  }

  return (
    <>
      <CategoryPathSynchronizer relatedAdsList={relatedAdsList} />

      <NoSsr>
        {shouldShowBanner && (
          <>
            {topBanner}
            {backgroundBanner}
            {catfishBanner}
          </>
        )}
        <Toast />
      </NoSsr>

      <PageTopControls>
        <Header
          isServerRendered={false}
          hasBurger={shouldShowBurgerInHeader}
          rightAction={renderHeaderActions()}
          {...(isLoadedWithoutAdData && { hasAddNew: true })}
        />
      </PageTopControls>

      <S.Wrapper>
        <S.Container>
          {isLoadedWithoutAdData ? (
            <MissingAdNotice />
          ) : (
            <>
              <Breadcrumbs
                hasBg
                hasHorizontalPadding={!isDesktop}
                breadcrumbs={buildBreadcrumbs(currentCategory)}
                nestingLvl={
                  getCurrentCategoryPath(router.query.slug).split('/').length
                }
              />

              {isDesktop && (
                <MainInfo
                  canBookmark={canBookmark}
                  isBookmarked={isFavorite}
                  onBookmarkClick={handleBookmarkClick}
                />
              )}

              <S.Grid>
                <S.Section>
                  {isAuthor && status === AdStatus.Pending && (
                    <WarningPendingState />
                  )}

                  <SliderWithGallery
                    isGalleryOpen={isGalleryOpen}
                    toggleGallery={toggleGallery}
                    canBookmark={canBookmark}
                    isBookmarked={isFavorite}
                    toggleBookmark={handleBookmarkClick}
                    {...(status === AdStatus.Expired &&
                      !isAuthor && { overlayText: t('common.ad_inactive') })}
                  />
                  {isDesktop && (
                    <>
                      <Properties />
                      {adData?.body && (
                        <>
                          <S.Divider />
                          <Description />
                        </>
                      )}
                      {!isAuthor && (
                        <>
                          <S.Divider />
                          <Overview />
                        </>
                      )}
                      {canComplain && (
                        <>
                          <S.Divider />
                          <Complaint />
                        </>
                      )}
                    </>
                  )}
                </S.Section>

                <S.Section>
                  <S.StickyContainer>
                    {status && <Status status={status} isAuthor={isAuthor} />}

                    <S.AdViewMain>
                      {isAuthor ? (
                        <>
                          {!isDesktop && <MainInfo />}
                          <Statistics />
                          <Settings />
                          {!isDesktop && <Properties />}
                          {!isDesktop && <Description />}
                          <AuthorInfo />
                        </>
                      ) : (
                        <>
                          {!isDesktop && <MainInfo />}
                          {isDesktop && <PriceAndContacts />}
                          {!isDesktop && <Properties />}
                          {!isDesktop && <Description />}
                          <AuthorInfo />
                          {!isDesktop && <Overview />}
                          {canComplain && !isDesktop && <Complaint />}
                        </>
                      )}
                    </S.AdViewMain>
                  </S.StickyContainer>
                </S.Section>
              </S.Grid>

              {isDesktop ? (
                <CollectionMasterModal
                  isOpen={isCollectionMasterOpen}
                  onClose={() => setIsCollectionMasterOpen(false)}
                />
              ) : (
                <CollectionMasterSheet
                  isOpen={isCollectionMasterOpen}
                  onClose={() => setIsCollectionMasterOpen(false)}
                />
              )}

              {hasFloatingButtons && <FloatingButtons />}
            </>
          )}

          {isRelatedAdsVisible && (
            <RelatedAds
              ads={relatedAdsList}
              isLoading={relatedAdsLoading}
              chunkSize={MAX_RELATED_ADS}
              title={t(
                `pages.ad.${
                  hasVisibleShop && status === AdStatus.Published
                    ? 'shop_ads'
                    : 'similar_ads'
                }`
              )}
              showAllLink={hasShowAllLink ? relatedAdsShowAllLink : undefined}
              transitionSource={
                hasVisibleShop
                  ? TransitionSourceBlockEnum.OtherUserAds
                  : TransitionSourceBlockEnum.SimilarAds
              }
            />
          )}

          {!isDesktop && (
            <Footer {...(hasFloatingButtons && { bottomGutter: '100px' })} />
          )}
        </S.Container>
        {isDesktop && <S.DesktopFooter />}
      </S.Wrapper>

      <PaymentInTerminalsAfterAuth adId={adData?.id} />

      <WebViewPaymentStatusModal />

      <SuccessPaymentStatusModal />
    </>
  );
};
